import { useQuery, useMutation } from '@apollo/client';
import CatalogContext from '@nile/banyan/src/Context/CatalogContext';
import {
  DropdownIndicator,
  formatDataForSelectDropdown,
  formatGroupLabel,
  getTranslatedText,
  IndicatorSeparator,
  SuperPop,
  TEXT_LENGTH,
  CreatableDropdown as CustomCreatableDropdown,
  isAuthorised,
  AppContext,
  EventEmitter,
  AccessControl,
  useProduct
} from '@nile/components';
import {
  AccountClient,
  GET_ALL_TAX,
  GET_SERVICE_CATEGORIES,
  UPDATE_SERVICE_CATEGORY,
  CREATE_SERVICE_CATEGORY,
  DELETE_SERVICE_CATEGORY,
  GET_NEXT_BILL_DATE
} from '@nile/graphql';
import {
  Button,
  ButtonGroup,
  CreatableDropdown,
  Dropdown,
  FormLayout,
  Heading,
  Label,
  TextField,
  Banner,
  TextStyle
} from '@superops/ui-design-system';
import { Tooltip } from 'react-clone-tippy';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { includes, omit } from 'lodash';
import React, { useContext, useState, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { ContentWrapper } from 'sopslibrary';
import { ServiceItemSheetContext } from '@nile/client_management/src/Pages/ContractTemplate/NewServiceItemSheet';
import TaxConfiguration from './TaxConfiguration';
import {
  BLOCK_HOUR_SUBSCRIPTION_TYPES,
  BLOCK_HOUR_TYPES,
  SUBSCRIPTION_TYPES,
  CUSTOM_SUBSCRIPTION_TYPES
} from './constants';
import HourTypeSection from './HourType';
import NewBlockHourSection from './NewBlockHourSection';
import NewBlockMoneySection from './NewBlockMoneySection';
import NewUnitTypeSection from './NewUnitType';

function getNextBillDate(input) {
  return AccountClient.query({
    query: GET_NEXT_BILL_DATE,
    variables: {
      input: input
    }
  }).then((response) => {
    return response?.data?.getNextBillDate;
  });
}

/**
 * This form is used to collect data for service items in places like Service catalog,
 * templates, client contracts etc. Pass configurable sections array to show or hide certain sections.
 */
function CatalogForm(props) {
  const {
    intl,
    serviceItem,
    actions,
    errors,
    configurableSections,
    disabledSections,
    flags,
    siteList,
    disableAllSections,
    disableEditEndDate,
    totalAssets,
    totalUsers,
    clientId,
    formType,
    editAndDelete,
    onlyProduct,
    integrationAddOn,
    isIntegrationAddOnItem,
    entryPoint,
    subscriptionItemQuantityCount,
    isSiteSpecificEnabled,
    refetchSubscriptionQuantity,
    isVisible,
    contractServiceItemData
  } = props;
  const {
    name,
    invoiceDescription,
    category,
    quantity,
    quantityType,
    roundUpValue,
    unitPrice,
    subscriptionType,
    useAsWorklogItem,
    businessHoursUnitPrice,
    afterHoursUnitPrice,
    carryForwardRemainingBlockHours,
    selectedSites,
    salesTax,
    salesTaxEnabled,
    billDate,
    frequency,
    endDate,
    canEdit,
    nextBillDate: nextBillDateFromProps
  } = serviceItem;

  const serviceItemSheetCtx = useContext(ServiceItemSheetContext);

  const appContext = useContext(AppContext);
  const { user } = appContext;
  const isMSPProduct = useProduct('MSP');
  const roleFeatures = user?.role?.roleFeatureList || [];

  const [serviceCategoryOptions, setServiceCategoryOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [nextBillDate, setNextBillDate] = useState(nextBillDateFromProps);
  const [nextBillDateLoading, setNextBillDateLoading] = useState(null);

  const context = props.context || useContext(CatalogContext);

  const handleSelectChange = (item, newValue) => {
    if (newValue) {
      context.updateProperties({
        category: {
          name: item.value,
          categoryId: item.categoryId
        }
      });
    } else {
      context.updateProperties({
        category: {
          id: item.id,
          name: item.value
        }
      });
    }
  };

  const [createServiceCategory] = useMutation(CREATE_SERVICE_CATEGORY, {
    client: AccountClient,
    onCompleted({ createServiceCategory }) {
      if (createServiceCategory) {
        const optionsCopy = [...serviceCategoryOptions];
        const optionCreatedIndex = serviceCategoryOptions.findIndex(
          ({ value }) => value === createServiceCategory.name
        );
        if (optionCreatedIndex > -1) {
          optionsCopy[optionCreatedIndex].id = createServiceCategory.categoryId;
          optionsCopy[optionCreatedIndex].categoryId =
            createServiceCategory.categoryId;
          optionsCopy[optionCreatedIndex].isNew = false;
          setServiceCategoryOptions(optionsCopy);
        }
        if (selectedOptions[0]?.value === createServiceCategory.name) {
          setSelectedOptions([optionsCopy[optionCreatedIndex]]);
          handleSelectChange(optionsCopy[optionCreatedIndex], true);
        }
      }
    }
  });

  const [updateServiceCategory] = useMutation(UPDATE_SERVICE_CATEGORY, {
    client: AccountClient
  });

  const [deleteServiceCategory] = useMutation(DELETE_SERVICE_CATEGORY, {
    client: AccountClient
  });

  const ALL_SITES = {
    id: 'ALL_SITES',
    name: getTranslatedText(intl, 'client.contract.invoiceItems.allSites'),
    label: getTranslatedText(intl, 'client.contract.invoiceItems.allSites'),
    value: null
  };

  const subscriptionTypes = SUBSCRIPTION_TYPES.map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.label)
    };
  });
  const customSubscriptionTypes = CUSTOM_SUBSCRIPTION_TYPES.map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.label)
    };
  });
  const blockHourSubscriptionTypes = BLOCK_HOUR_SUBSCRIPTION_TYPES.map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.label)
    };
  });
  const applicableBlockHourTypes = BLOCK_HOUR_TYPES.map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.label)
    };
  });
  const handlePropChange = (property, value) => {
    context.updateProperties({ [property]: value });
  };
  const handlePropertiesChange = (properties) => {
    context.updateProperties(properties);
  };

  useEffect(() => {
    if (
      (isSiteSpecificEnabled && serviceItem.unitType === 'ALL_USER') ||
      serviceItem?.assetView?.id ||
      serviceItem.unitType === 'INTEGRATION_USAGE'
    ) {
      const siteValues = selectedSites
        ? selectedSites
            .map((el) => {
              return {
                id: el.id
              };
            })
            .filter((el) => el.id !== 'ALL_SITES')
        : undefined;
      refetchSubscriptionQuantity({
        variables: {
          input: {
            client: {
              accountId: clientId
            },
            contractServiceItem: {
              itemId: contractServiceItemData?.itemId
            },
            sites: siteValues?.length ? siteValues : undefined,
            unitType: serviceItem.unitType,
            assetView:
              serviceItem.unitType === 'ASSET_VIEW'
                ? { id: serviceItem?.assetView?.id }
                : null
          }
        }
      });
    }
  }, [
    selectedSites,
    serviceItem?.unitType,
    serviceItem?.assetView?.id,
    contractServiceItemData?.itemId
  ]);

  function fetchNextBillDate() {
    if (frequency && frequency?.quantity && billDate) {
      setNextBillDateLoading(true);
      const input = {
        frequency: frequency,
        billDate: billDate,
        nextBillDate: nextBillDateFromProps,
        compute: !disableAllSections
      };
      if (endDate) {
        input.endDate = endDate;
      }
      getNextBillDate(input).then((response) => {
        setNextBillDate(response);
        setNextBillDateLoading(false);
      });
    }
  }

  useEffect(() => {
    if (!disableAllSections || nextBillDateFromProps !== null) {
      fetchNextBillDate();
    }
  }, [billDate, frequency, endDate]);

  useEffect(() => {
    EventEmitter.subscribe('fetchNextBillDate', () => {
      fetchNextBillDate();
    });
    return () => {
      EventEmitter.unsubscribe('fetchNextBillDate', () => {
        fetchNextBillDate();
      });
    };
  }, []);

  const { loading, data, error } = useQuery(GET_SERVICE_CATEGORIES, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted({ getServiceCategories }) {
      if (getServiceCategories) {
        const serviceCategories = getServiceCategories.map((el) => {
          return {
            ...el,
            value: el.name,
            label: el.name,
            editState: false,
            id: el.categoryId || el.id
          };
        });
        setServiceCategoryOptions(serviceCategories);
        const selectedOption = serviceCategories.find((el) => {
          if (el.id) return el.id === category?.categoryId;
          return el.name === category?.name;
        });
        if (selectedOption) setSelectedOptions([selectedOption]);
      }
    }
  });
  const { loading: taxLoading, error: taxError, data: taxData } = useQuery(
    GET_ALL_TAX,
    {
      client: AccountClient,
      skip: !isMSPProduct
    }
  );
  const taxRateList = taxData?.getAllTax
    ? formatDataForSelectDropdown(taxData?.getAllTax)
    : [];
  const serviceCategoryList =
    data?.getServiceCategories?.map((el) => {
      return {
        ...el,
        value: el.name,
        label: el.name
      };
    }) || [];

  const Tag = props.tag || ContentWrapper;
  // Include all sites option along with site options
  const siteOptions = [ALL_SITES].concat(
    formatDataForSelectDropdown(siteList || [])
  );
  const isIntegrationAddOn = isIntegrationAddOnItem;
  const hasBanner =
    formType !== 'service-catalog' && formType !== 'contract-template';
  return (
    <Tag nopadding gap="80px" bgcolor="var(--sops-white-transparent-bg)">
      <div
        className={`flex center ${
          formType === 'service-catalog' || formType === 'contract-template'
            ? 'w50'
            : ''
        }`}
      >
        <div className={`w100 ${hasBanner ? 'mr80' : 'm-0'}`}>
          <Heading element="h2">
            <FormattedMessage id="admin.serviceCatalog.basicInfo" />
          </Heading>
          <FormLayout>
            <div className="mt-20 mb-20">
              <TextField
                requiredIndicator
                value={name}
                id="serviceItemName"
                onChange={handlePropChange.bind(this, 'name')}
                label={getTranslatedText(intl, 'common.name')}
                placeholder={getTranslatedText(
                  intl,
                  'admin.serviceCatalog.itemName.placeholder'
                )}
                name="Name"
                type="text"
                disabled={
                  isIntegrationAddOn ||
                  (disabledSections && includes(disabledSections, 'name'))
                }
                error={errors?.name?.[0]}
                maxLength={TEXT_LENGTH.size_5}
              />
            </div>

            <div className="mb-20">
              <TextField
                value={invoiceDescription}
                id="serviceItemDescription"
                onChange={handlePropChange.bind(this, 'invoiceDescription')}
                label={getTranslatedText(
                  intl,
                  'admin.serviceCatalog.itemDescription'
                )}
                placeholder={getTranslatedText(
                  intl,
                  'admin.serviceCatalog.itemDescription.placeholder'
                )}
                name="invoiceDescription"
                type="text"
                disabled={
                  isIntegrationAddOn ||
                  (disabledSections &&
                    includes(disabledSections, 'description'))
                }
                error={errors?.invoiceDescription?.[0]}
                maxLength={TEXT_LENGTH.size_9}
              />
            </div>
            <div spacing="tight" id="serviceCategorySelect" className="mb-20">
              <Label>
                <FormattedMessage id="admin.serviceCatalog.serviceCategory" />
              </Label>
              {editAndDelete ? (
                <CustomCreatableDropdown
                  selectedOptions={selectedOptions}
                  options={serviceCategoryOptions}
                  updateOptionsList={(modifiedOptions) => {
                    setServiceCategoryOptions(modifiedOptions);
                  }}
                  updateSelectedOptions={(selectedOptions) => {
                    const selectedOption = selectedOptions[0];
                    if (selectedOption) {
                      handleSelectChange(selectedOption, true);
                      setSelectedOptions([selectedOption]);
                    } else {
                      handleSelectChange({ value: null }, true);
                      setSelectedOptions([]);
                    }
                  }}
                  isCreate={isAuthorised(roleFeatures, 'serviceCatalog.add')}
                  isEdit={isAuthorised(roleFeatures, 'serviceCatalog.edit')}
                  isDelete={isAuthorised(roleFeatures, 'serviceCatalog.delete')}
                  onOptionCreate={(option) => {
                    createServiceCategory({
                      input: {
                        category: { name: option.value }
                      },
                      variables: {
                        category: { name: option.value }
                      }
                    });
                  }}
                  onOptionEdit={(option) => {
                    updateServiceCategory({
                      input: {
                        categoryId: option.id,
                        category: { categoryId: option.id, name: option.value }
                      },
                      variables: {
                        categoryId: option.id,
                        category: { categoryId: option.id, name: option.value }
                      }
                    });
                  }}
                  onOptionDelete={(option) => {
                    deleteServiceCategory({
                      input: {
                        categoryIds: [option.id]
                      },
                      variables: {
                        categoryIds: [option.id]
                      }
                    });
                  }}
                  disabled={
                    isIntegrationAddOn ||
                    (disabledSections &&
                      includes(disabledSections, 'serviceCategory'))
                  }
                />
              ) : (
                <CreatableDropdown
                  isClearable
                  value={serviceCategoryList.find((el) => {
                    if (el.id) return el.id === category?.id;
                    return el.name === category?.name;
                  })}
                  onChange={handleSelectChange}
                  placeholder={getTranslatedText(
                    intl,
                    'admin.serviceCatalog.serviceCategoryPlaceholder'
                  )}
                  name="serviceCategory"
                  options={serviceCategoryList}
                  classNamePrefix="value_selector"
                  components={{
                    IndicatorSeparator,
                    DropdownIndicator
                  }}
                  formatGroupLabel={formatGroupLabel}
                  menuPlacement="auto"
                  closeMenuOnScroll={(e) => {
                    return (
                      e.target &&
                      e.target.classList &&
                      !(e.target.classList.value.indexOf('menu-list') > -1)
                    );
                  }}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 })
                  }}
                  isDisabled={
                    isIntegrationAddOn ||
                    (disabledSections &&
                      includes(disabledSections, 'serviceCategory'))
                  }
                />
              )}
              {includes(configurableSections, 'siteSelection') && (
                <div className="mt20 flex center full-width">
                  <div className="full-width">
                    <Label>
                      <FormattedMessage id="admin.serviceCatalog.sites" />
                    </Label>
                    <Dropdown
                      className={``}
                      classNamePrefix="value_selector"
                      formatGroupLabel={formatGroupLabel}
                      components={{ IndicatorSeparator, DropdownIndicator }}
                      title={getTranslatedText(
                        intl,
                        'admin.serviceCatalog.sites'
                      )}
                      onChange={(selectedSiteList, el1) => {
                        let selectedItemArray = selectedSiteList.map((el) => {
                          return omit(el, ['value', 'label']);
                        });
                        const allSiteIndex = selectedItemArray.findIndex(
                          (el) => el.id === 'ALL_SITES'
                        );
                        if (allSiteIndex === 0) {
                          selectedItemArray.splice(0, 1);
                        } else if (allSiteIndex > 0) {
                          selectedItemArray = [ALL_SITES];
                        }
                        handlePropChange('selectedSites', selectedItemArray);
                      }}
                      options={siteOptions}
                      placeholder={getTranslatedText(
                        intl,
                        'admin.serviceCatalog.selectSites'
                      )}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                      closeMenuOnScroll={(e) => {
                        return (
                          e.target &&
                          e.target.classList &&
                          !(e.target.classList.value.indexOf('menu-list') > -1)
                        );
                      }}
                      value={
                        selectedSites
                          ? selectedSites.map((el) => {
                              return {
                                ...el,
                                value: el.name,
                                label: el.name
                              };
                            })
                          : [ALL_SITES]
                      }
                      name="valueselector"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 })
                      }}
                      noOptionsMessage={() => {
                        return loading ? 'Loading...' : 'No options';
                      }}
                      isMulti
                      isClearable={false}
                      isDisabled={isIntegrationAddOn}
                    />
                  </div>
                  <div className="ml-10 self-end mb4">
                    <SuperPop
                      position="top-start"
                      content={getTranslatedText(
                        intl,
                        'client.invoice.settings.siteSpecificInvoice.superTip'
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          </FormLayout>
        </div>
        <AccessControl
          productToCheck="MSP"
          render={
            <>
              {formType !== 'service-catalog' &&
                formType !== 'contract-template' && (
                  <Banner status="info">
                    <div className="mb20 bold">
                      <FormattedMessage id="client.contract.catalog.fieldExplanation.title" />
                    </div>
                    <p>
                      <FormattedMessage
                        id="client.contract.catalog.fieldExplanation.content"
                        values={{
                          br: <br />,
                          b: (chunks) => <b>{chunks}</b>,
                          p: (chunks) => <p>{chunks}</p>
                        }}
                      />
                    </p>
                  </Banner>
                )}
            </>
          }
        />
      </div>

      <AccessControl
        productToCheck="MSP"
        render={
          <>
            <div className="full-width">
              <Heading element="h2">
                <FormattedMessage id="admin.serviceCatalog.itemProperties" />
              </Heading>

              <div className="mt8">
                <div>
                  <TextStyle variation="subdued">
                    <FormattedMessage id="admin.serviceCatalog.quantityType" />
                  </TextStyle>
                  <div className="btn-split mt10">
                    <Tooltip
                      html={
                        <p
                          dangerouslySetInnerHTML={{
                            __html: getTranslatedText(
                              intl,
                              'admin.catalog.items.restrictDefaultItemEditOrDelete'
                            )
                          }}
                        />
                      }
                      disabled={!serviceItem?.isDefault || isIntegrationAddOn}
                      inertia
                      trigger="mouseenter"
                      animation="shift"
                      animateFill={false}
                      className="tooltip"
                      position="top-start"
                    >
                      <ButtonGroup segmented>
                        <div
                          className={
                            quantityType === 'UNITS' ? 'isActivated' : ''
                          }
                        >
                          <Button
                            outline={quantityType === 'UNITS'}
                            onClick={() => {
                              handlePropChange('quantityType', 'UNITS');
                            }}
                            disabled={
                              isIntegrationAddOn ||
                              serviceItem?.isDefault ||
                              (disabledSections &&
                                includes(
                                  disabledSections,
                                  'changeServiceItemType'
                                ))
                            }
                          >
                            <FormattedMessage id="admin.serviceCatalog.quantityType.units" />
                          </Button>
                        </div>
                        {includes(configurableSections, 'worklogHours') && (
                          <div
                            className={
                              quantityType === 'HOURS' ? 'isActivated' : ''
                            }
                          >
                            <Button
                              outline={quantityType === 'HOURS'}
                              onClick={() => {
                                handlePropChange('quantityType', 'HOURS');
                              }}
                              disabled={
                                onlyProduct ||
                                isIntegrationAddOn ||
                                serviceItem?.isDefault ||
                                (disabledSections &&
                                  includes(
                                    disabledSections,
                                    'changeServiceItemType'
                                  ))
                              }
                            >
                              <FormattedMessage id="admin.serviceCatalog.quantityType.worklogHours" />
                            </Button>
                          </div>
                        )}
                        <div
                          className={
                            quantityType === 'BLOCK_HOURS' ? 'isActivated' : ''
                          }
                        >
                          <Button
                            outline={quantityType === 'BLOCK_HOURS'}
                            onClick={() => {
                              handlePropChange('quantityType', 'BLOCK_HOURS');
                            }}
                            disabled={
                              onlyProduct ||
                              isIntegrationAddOn ||
                              serviceItem?.isDefault ||
                              (disabledSections &&
                                includes(
                                  disabledSections,
                                  'changeServiceItemType'
                                ))
                            }
                          >
                            <FormattedMessage id="admin.serviceCatalog.quantityType.blockHours" />
                          </Button>
                        </div>

                        <Button
                          outline={quantityType === 'BLOCK_MONEY'}
                          onClick={() => {
                            handlePropChange('quantityType', 'BLOCK_MONEY');
                          }}
                          disabled={
                            onlyProduct ||
                            isIntegrationAddOn ||
                            serviceItem?.isDefault ||
                            (disabledSections &&
                              includes(
                                disabledSections,
                                'changeServiceItemType'
                              ))
                          }
                        >
                          <FormattedMessage id="admin.serviceCatalog.quantityType.blockMoney" />
                        </Button>
                      </ButtonGroup>
                    </Tooltip>
                  </div>
                </div>
              </div>
              {quantityType === 'UNITS' && (
                <NewUnitTypeSection
                  configurableSections={configurableSections}
                  disabledSections={disabledSections || []}
                  serviceItem={serviceItem}
                  nextBillDate={nextBillDate}
                  nextBillDateLoading={nextBillDateLoading}
                  handlePropChange={handlePropChange}
                  handlePropertiesChange={handlePropertiesChange}
                  subscriptionTypes={subscriptionTypes}
                  customSubscriptionTypes={customSubscriptionTypes}
                  isDisabled={disableAllSections}
                  isEditEndDatedisabled={disableEditEndDate}
                  totalAssets={totalAssets}
                  totalUsers={totalUsers}
                  subscriptionItemQuantityCount={subscriptionItemQuantityCount}
                  isSiteSpecificEnabled={isSiteSpecificEnabled}
                  refetchSubscriptionQuantity={refetchSubscriptionQuantity}
                  clientId={clientId}
                  formType={formType}
                  pax8={props.pax8}
                  enableReminder={props.enableReminder}
                  errors={props.errors}
                  integrationAddOn={integrationAddOn}
                  isIntegrationAddOnItem={isIntegrationAddOnItem}
                  entryPoint={entryPoint}
                />
              )}
              {quantityType === 'HOURS' && (
                <HourTypeSection
                  configurableSections={configurableSections}
                  disabledSections={disabledSections || []}
                  serviceItem={serviceItem}
                  nextBillDate={nextBillDate}
                  nextBillDateLoading={nextBillDateLoading}
                  handlePropChange={handlePropChange}
                  subscriptionTypes={subscriptionTypes}
                  customSubscriptionTypes={customSubscriptionTypes}
                  isDisabled={disableAllSections}
                  isEditEndDatedisabled={disableEditEndDate}
                  clientId={clientId}
                  formType={formType}
                  errors={props.errors}
                />
              )}
              {quantityType === 'BLOCK_HOURS' && (
                <NewBlockHourSection
                  configurableSections={configurableSections}
                  disabledSections={disabledSections || []}
                  serviceItem={serviceItem}
                  nextBillDate={nextBillDate}
                  nextBillDateLoading={nextBillDateLoading}
                  handlePropChange={handlePropChange}
                  handlePropertiesChange={handlePropertiesChange}
                  subscriptionTypes={blockHourSubscriptionTypes}
                  customSubscriptionTypes={customSubscriptionTypes}
                  applicableBlockHourTypes={applicableBlockHourTypes}
                  isDisabled={disableAllSections}
                  isEditEndDatedisabled={disableEditEndDate}
                  clientId={clientId}
                  formType={formType}
                  enableReminder={props.enableReminder}
                  errors={props.errors}
                />
              )}
              {quantityType === 'BLOCK_MONEY' && (
                <NewBlockMoneySection
                  configurableSections={configurableSections}
                  disabledSections={disabledSections || []}
                  serviceItem={serviceItem}
                  nextBillDate={nextBillDate}
                  nextBillDateLoading={nextBillDateLoading}
                  handlePropChange={handlePropChange}
                  handlePropertiesChange={handlePropertiesChange}
                  subscriptionTypes={blockHourSubscriptionTypes}
                  customSubscriptionTypes={customSubscriptionTypes}
                  applicableBlockHourTypes={applicableBlockHourTypes}
                  isDisabled={disableAllSections}
                  isEditEndDatedisabled={disableEditEndDate}
                  clientId={clientId}
                  formType={formType}
                  enableReminder={props.enableReminder}
                  errors={props.errors}
                />
              )}
            </div>
            <div className="pb-100">
              <Heading element="h2">
                <FormattedMessage id="admin.serviceCatalog.taxConfiguration" />
              </Heading>
              <TaxConfiguration
                salesTaxEnabled={salesTaxEnabled}
                salesTax={salesTax}
                disabledSections={disabledSections}
                handlePropChange={handlePropChange}
              />
            </div>
          </>
        }
      />
    </Tag>
  );
}

export default injectIntl(withLDConsumer()(CatalogForm));
